import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchWorkspaces, updateWorkspace } from '../../redux/Slices/workspaceSlice';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { find } from '../../utils/helpers';
import WorkspaceWhiteLabelConfig from './WorkspaceWhiteLabelConfig';
import Edit from '../icons/Edit';
import Spinner from '../Global/Spinner';
import './scss/WorkspaceSettings.scss';

const WorkspaceSettings = () => {
  const dispatch = useDispatch();
  const { shortId } = useParams();

  const { workspaces } = useSelector(state => state.workspaces);
  const { user } = useSelector(state => state.user);

  const [isEditingWorkspaceName, setIsEditingWorkspaceName] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceUpdateStatus, setWorkspaceUpdateStatus] = useState('saved');
  const [loading, setLoading] = useState(true);

  // Fetch workspaces if not loaded
  useEffect(() => {
    if (workspaces.length === 0) {
      dispatch(fetchWorkspaces())
        .unwrap()
        .catch((error) => {
          console.error('Error fetching workspaces:', error);
          dispatch(showSnackBar({
            message: 'Failed to load workspace',
            type: 'error'
          }));
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [dispatch, workspaces.length]);

  // Get current workspace
  const workspace = find.get(workspaces, { name: 'workspace', workspace: shortId }) || null;

  // Initialize workspace name when workspace changes
  useEffect(() => {
    if (workspace) {
      setWorkspaceName(workspace.name);
    }
  }, [workspace]);

  // Handle workspace name update
  const handleUpdateWorkspaceName = async () => {
    if (!workspace) return;

    if (!workspaceName.trim()) {
      dispatch(showSnackBar({
        message: 'Workspace name cannot be empty',
        type: 'error'
      }));
      setWorkspaceName(workspace.name); // Reset to original
      setIsEditingWorkspaceName(false);
      return;
    }

    if (workspaceName.trim() === workspace.name) {
      setIsEditingWorkspaceName(false);
      return;
    }
 
    try {
      setWorkspaceUpdateStatus('saving');

      await dispatch(updateWorkspace({
        workspaceId: workspace.shortId,
        name: workspaceName.trim()
      })).unwrap();

      setWorkspaceUpdateStatus('saved');
      setIsEditingWorkspaceName(false);

      dispatch(showSnackBar({
        message: 'Workspace name updated successfully',
        type: 'success'
      }));

      // Reset status after 2 seconds
      setTimeout(() => {
        setWorkspaceUpdateStatus('saved');
      }, 2000);

    } catch (error) {
      console.error('Error updating workspace name:', error);
      setWorkspaceUpdateStatus('error');
      setWorkspaceName(workspace.name); // Reset to original

      dispatch(showSnackBar({
        message: error.message || 'Failed to update workspace name',
        type: 'error'
      }));

      // Reset status after 3 seconds
      setTimeout(() => {
        setWorkspaceUpdateStatus('saved');
      }, 3000);
    }
  };

  // Handle keyboard events for workspace name input
  const handleWorkspaceNameKeyDown = (e) => {
    if (!workspace) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      handleUpdateWorkspaceName();
    } else if (e.key === 'Escape') {
      setWorkspaceName(workspace.name); // Reset to original
      setIsEditingWorkspaceName(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="workspace-settings-page">
        <div className="workspace-settings-loading">
          <Spinner />
        </div>
      </div>
    );
  }

  // Workspace not found
  if (!workspace) {
    return (
      <div className="workspace-settings-page">
        <div className="workspace-settings-error">
          <h2>Workspace Not Found</h2>
          <p>The workspace you're looking for doesn't exist or you don't have access to it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-settings-page">
      <div className="workspace-settings-container">
        {/* Workspace Name Section */}
        <div className="setting-row">
          <div className="setting-label">
            <h2>Workspace Name</h2>
            <p>Update your workspace name to better reflect your team or project.</p>
          </div>
          <div className="workspace-name-content">
            <div className="workspace-name-edit">
              {isEditingWorkspaceName ? (
                <input
                  type="text"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  onBlur={handleUpdateWorkspaceName}
                  onKeyDown={handleWorkspaceNameKeyDown}
                  className="workspace-name-input"
                  autoFocus
                  maxLength={50}
                />
              ) : (
                <div
                  className="workspace-name-display"
                  onClick={() => setIsEditingWorkspaceName(true)}
                >
                  <span className="workspace-name-text">{workspace.name}</span>
                  <Edit size={16} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Auto-save Status */}
        {/* {workspaceUpdateStatus && workspaceUpdateStatus !== 'saved' && (
          <div className="setting-row">
            <div className="setting-label"></div>
            <div className="auto-save-status">
              {workspaceUpdateStatus === 'saving' && (
                <span className="save-status saving">
                  <span className="material-symbols-outlined">sync</span>
                  Saving...
                </span>
              )}
              {workspaceUpdateStatus === 'error' && (
                <span className="save-status error">
                  <span className="material-symbols-outlined">error</span>
                  Save failed
                </span>
              )}
            </div>
          </div>
        )} */}

        {/* White Label Configuration Section */}
        <WorkspaceWhiteLabelConfig
          workspace={workspace}
          currentUser={user}
        />
      </div>
    </div>
  );
};

export default WorkspaceSettings;

